"use client";

import React from "react";
import TradeSectionTitleSkeleton from "modules/lgs/components/stores/skeletons/TradeSectionTitleSkeleton";
import { TradeListTableSkeleton } from "modules/tradeLists/components/skeletons/TradeListTableSkeleton";
import TradePreviewCardSkeleton from "modules/common/components/skeletons/trade/TradePreviewCardSkeleton";
import { useViewPreference } from "modules/common/stores/useCommonStore";
import { cn } from "utils/utils";
import { ViewIndex } from "modules/common/model/viewTypes";

interface LGSSectionSkeletonProps {
  title?: string;
  type: "match" | "trades" | "wants";
  rowCount?: number;
  className?: string;
  /** When true, removes the card wrapper styling on mobile */
  hideCard?: boolean;
  /** View index from SSR cookie — shows correct skeleton immediately without hydration wait */
  initialViewIndex?: ViewIndex;
  /** Mirror the real section's `countInMainbar` — hides the title row on desktop */
  countInMainbar?: boolean;
}

export function LGSSectionSkeleton({
  title,
  type,
  rowCount = 9,
  className,
  hideCard = false,
  initialViewIndex,
  countInMainbar = false,
}: LGSSectionSkeletonProps) {
  const { viewPreference } = useViewPreference();
  // initialViewIndex (from SSR cookie) is authoritative when provided.
  // Falls back to Zustand once it hydrates on the client.
  const isVisualView = (initialViewIndex ?? viewPreference) === 1;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {title && (
        <TradeSectionTitleSkeleton
          title={title}
          type={type}
          showMobileFilterBar
          countInMainbar={countInMainbar}
        />
      )}
      <div className="flex-1 overflow-hidden">
        {/* Same layout at every width — the sections themselves render a grid or
            a table from the view preference, never a carousel. */}
        {isVisualView ? (
          <div className="@container h-full overflow-y-auto">
            {/* px-1 / md:py-0 track ResponsiveCardDisplayGrid's row padding and
                scroll-container padding, so the first card row doesn't move
                when the real grid swaps in. */}
            <div className="@min-[600px]:grid-cols-3 @min-[960px]:grid-cols-4 @min-[1280px]:grid-cols-5 @min-[1560px]:grid-cols-7 @min-[1920px]:grid-cols-9 grid auto-rows-min grid-cols-2 gap-2 px-1 py-2 md:py-0">
              {Array.from({ length: 20 }).map((_, i) => (
                <TradePreviewCardSkeleton key={i} width="100%" />
              ))}
            </div>
          </div>
        ) : (
          <TradeListTableSkeleton
            isMatchedTrades={type === "match"}
            isTradeList={false}
            rowCount={rowCount}
            containerClassName="relative max-h-full overflow-y-auto"
          />
        )}
      </div>
    </div>
  );
}
